import {engineContext} from './context.js';

(function(g){'use strict';
const VERSION='0.1.0-alpha';
const cp=x=>x==null?x:JSON.parse(JSON.stringify(x));

function normalizeScope(scope){
  return ['OWNED','WATCHLIST','RESEARCH'].includes(scope)?scope:'RESEARCH';
}
function scopeWeight(scope){
  return scope==='OWNED'?3:scope==='WATCHLIST'?2:1;
}
function priorityScore(item={}){
  const p=Number.isFinite(item.priority)?item.priority:0;
  const change=Number.isFinite(item.changePriority)?item.changePriority:0;
  const event=Number.isFinite(item.eventPriority)?item.eventPriority:0;
  const blocked=item.blocked?50:0;
  return scopeWeight(normalizeScope(item.scope))*100 + p + change + event + blocked;
}
function estimateMinutes(item={}){
  const m=Number(item.estimatedMinutes);
  return Number.isFinite(m)&&m>0?m:5;
}

function create(opts={}){
  const pipeline=opts.pipeline;
  if(!pipeline||typeof pipeline.run!=='function') throw new Error('PIPELINE_REQUIRED');
  const now=opts.now||(()=>new Date().toISOString());

  async function plan(items,budgetMinutes=60){
    const list=Array.isArray(items)?items.map(cp):[];
    const ranked=list.map(x=>({...x,scope:normalizeScope(x.scope),_score:priorityScore(x),_minutes:estimateMinutes(x)}))
      .sort((a,b)=>b._score-a._score || a.symbol.localeCompare(b.symbol));

    let used=0;
    const selected=[],deferred=[];
    for(const item of ranked){
      if(used+item._minutes<=budgetMinutes){
        selected.push(item); used+=item._minutes;
      }else{
        deferred.push(item);
      }
    }
    return Object.freeze({
      budgetMinutes,
      usedMinutes:used,
      remainingMinutes:Math.max(0,budgetMinutes-used),
      selected:Object.freeze(selected.map(cp)),
      deferred:Object.freeze(deferred.map(cp))
    });
  }

  async function runBatch(items,budgetMinutes=60){
    const batchId=`batch-${now()}`;
    const p=await plan(items,budgetMinutes);
    const results=[];

    for(const item of p.selected){
      try{
        const r=await pipeline.run(item);
        results.push(Object.freeze({
          symbol:item.symbol,
          scope:item.scope,
          score:item._score,
          estimatedMinutes:item._minutes,
          status:r&&r.status?r.status:'REVIEW',
          result:cp(r)
        }));
      }catch(e){
        results.push(Object.freeze({
          symbol:item.symbol,
          scope:item.scope,
          score:item._score,
          estimatedMinutes:item._minutes,
          status:'REVIEW',
          error:String(e&&e.message||e)
        }));
      }
    }

    const summary={
      batchId,
      selectedCount:p.selected.length,
      deferredCount:p.deferred.length,
      readyCount:results.filter(x=>x.status==='READY').length,
      reviewCount:results.filter(x=>x.status!=='READY').length,
      usedMinutes:p.usedMinutes,
      budgetMinutes:p.budgetMinutes
    };

    return Object.freeze({
      version:VERSION,
      batchId,
      plan:p,
      results:Object.freeze(results),
      summary:Object.freeze(summary)
    });
  }

  return Object.freeze({VERSION,plan,runBatch});
}

async function selfTest(){
  const t=[],ok=(n,p)=>t.push({name:n,pass:!!p});
  ok('owned outranks watchlist',priorityScore({scope:'OWNED'})>priorityScore({scope:'WATCHLIST'}));
  ok('watchlist outranks research',priorityScore({scope:'WATCHLIST'})>priorityScore({scope:'RESEARCH'}));
  ok('blocked adds urgency',priorityScore({scope:'RESEARCH',blocked:true})>priorityScore({scope:'RESEARCH'}));
  ok('default estimate is five',estimateMinutes({})===5);
  ok('explicit estimate preserved',estimateMinutes({estimatedMinutes:12})===12);

  const calls=[];
  const pipeline={run:async x=>{calls.push(x.symbol);return {status:x.symbol==='BBB'?'REVIEW':'READY'};}};
  const b=create({pipeline,now:()=> '2026-08-30T19:00:00+02:00'});

  const items=[
    {symbol:'AAA',scope:'OWNED',priority:10,estimatedMinutes:20},
    {symbol:'BBB',scope:'WATCHLIST',priority:50,estimatedMinutes:15},
    {symbol:'CCC',scope:'RESEARCH',priority:99,estimatedMinutes:30},
    {symbol:'DDD',scope:'OWNED',priority:1,estimatedMinutes:25}
  ];
  const p=await b.plan(items,40);
  ok('plan respects budget',p.usedMinutes<=40);
  ok('highest scope/priority selected first',p.selected[0].symbol==='AAA');
  ok('some items deferred',p.deferred.length>0);

  const r=await b.runBatch(items,40);
  ok('batch id deterministic from clock',r.batchId==='batch-2026-08-30T19:00:00+02:00');
  ok('pipeline called for selected only',calls.length===r.plan.selected.length);
  ok('summary selected count matches',r.summary.selectedCount===r.plan.selected.length);
  ok('summary deferred count matches',r.summary.deferredCount===r.plan.deferred.length);
  ok('ready plus review equals selected',r.summary.readyCount+r.summary.reviewCount===r.summary.selectedCount);

  const failing=create({pipeline:{run:async()=>{throw Error('PIPE_FAIL')}}});
  const fr=await failing.runBatch([{symbol:'ZZZ',scope:'OWNED',estimatedMinutes:5}],10);
  ok('pipeline failure isolated to one company',fr.results[0].status==='REVIEW');
  ok('pipeline failure preserved',fr.results[0].error==='PIPE_FAIL');

  let pe=false;try{create({})}catch(e){pe=e.message==='PIPELINE_REQUIRED'}
  ok('pipeline required',pe);

  const api=create({pipeline});
  ok('no trade signal',!('tradeSignal' in api));
  ok('no capital permission',!('capitalPermission' in api));
  ok('no universal score',!('score' in api));

  return Object.freeze({passed:t.filter(x=>x.pass).length,total:t.length,tests:Object.freeze(t)});
}

g.ZooInvestResearchBatchOrchestrator=Object.freeze({VERSION,normalizeScope,priorityScore,estimateMinutes,create,selfTest});
})(engineContext);